import './dialog.scss';

import {
  ReactNode,
  SyntheticEvent,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

import { locator } from '../../testing/e2e-locator/locator';
import { useScrollLock } from './hooks/useScrollLock';
import { DialogProps } from './types/DialogProps';

export const Dialog = ({
  children,
  className = '',
  labelledBy,
  onClose,
  testId,
}: DialogProps): ReactNode => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const unmounting = useRef(false);

  useScrollLock();

  useLayoutEffect((): (() => void) => {
    const dialog = dialogRef.current;
    previouslyFocused.current = document.activeElement as HTMLElement | null;

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      // Safari < 15.4 has no showModal: same look, without the inert background
      dialog.setAttribute('open', '');
    }

    // showModal focuses the first button; start at the top of the dialog instead
    contentRef.current.focus();

    return (): void => {
      // Stops the close event this fires from reopening a dialog on its way out
      unmounting.current = true;

      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
    };
  }, []);

  // A passive cleanup runs after the commit, when React has already put focus
  // back where it was when the commit began and the page is no longer inert
  useEffect(
    (): (() => void) => (): void => {
      previouslyFocused.current?.focus();
    },
    [],
  );

  // On the document: without showModal, focus can leave the dialog
  useEffect((): (() => void) => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || event.isComposing) {
        return;
      }

      // Cancelling the keydown stops the browser closing the dialog itself
      event.preventDefault();

      if (onClose) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return (): void => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const handleBackdropClick = (): void => {
    if (onClose) {
      onClose();
    }
  };

  const handleCancel = (event: SyntheticEvent<HTMLDialogElement>): void => {
    event.preventDefault();

    if (onClose) {
      onClose();
    }
  };

  const handleNativeClose = (): void => {
    // Without user activation a close request cannot be cancelled, so reopen a
    // dialog that offers no way out
    if (!onClose && !unmounting.current) {
      dialogRef.current?.showModal();
      contentRef.current?.focus();
    }
  };

  return (
    <dialog
      aria-labelledby={labelledBy}
      aria-modal="true"
      className={`dialog ${className}`.trim()}
      data-testid={testId}
      ref={dialogRef}
      role="dialog"
      onCancel={handleCancel}
      onClose={handleNativeClose}
    >
      <div
        aria-hidden="true"
        className="dialog-backdrop"
        data-testid={locator.components.dialog.backdrop}
        onClick={handleBackdropClick}
      />
      <div className="dialog-content" ref={contentRef} tabIndex={-1}>
        {children}
      </div>
    </dialog>
  );
};
